import React, { useState, useMemo } from 'react';
import { useHGS } from '../context/HGSContext';
import { DEMO_FIXTURES, MODULE_SCHEMAS } from '../data/masterData';
import { RISK_UNIVERSE } from '../data/riskUniverse';
import { ClipboardCheck, ShieldAlert, FileSignature, Eye, Code2, CheckCircle2, X, FileSpreadsheet, Wand2, AlertTriangle, Compass } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Master Question Bank — Admin-only screen that surfaces the static master
 * question bank powering the demo. Four cards (Risk / DLC / Fraud / PCC).
 * Each shows count, source file, sheet list, and offers:
 *   • View Questions  — drawer listing every imported item with source row
 *   • View Source Mapping — JSON dump of the schema mapping
 *   • Status: Active Demo Master / Not Loaded — Risk is always Active (baked
 *     into RISK_UNIVERSE); Fraud / PCC / DLC depend on whether the fixture
 *     has been ingested into the live `masters.{key}` store via the
 *     "Ingest into Live Master" CTA.
 */

// Risk is sourced from RISK_UNIVERSE (always-active baseline), not from
// DEMO_FIXTURES. We build a compatible fixture shape so the drawer logic
// works uniformly across all four cards.
const RISK_FIXTURE = {
  fileName: RISK_UNIVERSE[0]?._sourceFile || 'Risk Templte 103Ki.xlsx',
  sheetNames: [...new Set(RISK_UNIVERSE.map((r) => r._sourceSheet))],
  rows: RISK_UNIVERSE,
};

const MODULES = [
  {
    key: 'risk',
    title: 'Risk Universe',
    subtitle: 'Parent governance layer — drives Fraud / PCC / DLC',
    icon: Compass,
    accent: '#CC0000',
    questionKey: 'description',
    idKey: 'id',
    categoryKey: 'category',
    countLabel: 'risks',
    fixture: RISK_FIXTURE,         // baked-in source (not DEMO_FIXTURES)
    alwaysActive: true,            // never shows "Not Loaded" / Ingest CTA
  },
  {
    key: 'dlc',
    title: 'DLC Self-Verification',
    subtitle: 'Quarterly division-level compliance declarations',
    icon: FileSignature,
    accent: '#7C3AED',
    questionKey: 'declarationQuestion',
    idKey: 'questionId',
    categoryKey: 'dlcCategory',
    countLabel: 'items',
  },
  {
    key: 'fraud',
    title: 'Fraud Prevention',
    subtitle: 'SOX-aligned anti-fraud control checklist',
    icon: ShieldAlert,
    accent: '#0EA5E9',
    questionKey: 'controlQuestion',
    idKey: 'controlId',
    categoryKey: 'fraudCategory',
    countLabel: 'controls',
  },
  {
    key: 'pcc',
    title: 'Product Compliance Data (PCC)',
    subtitle: 'Section-wise product compliance questionnaire',
    icon: ClipboardCheck,
    accent: '#1F2937',
    questionKey: 'complianceRequirement',
    idKey: 'questionId',
    categoryKey: 'sectionName',
    countLabel: 'controls',
  },
];

// Resolve the source-of-truth fixture for a module — either the inline
// fixture (Risk) or DEMO_FIXTURES (Fraud / PCC / DLC).
const fixtureFor = (m) => m.fixture || DEMO_FIXTURES[m.key];

export default function QuestionBankPage() {
  const { masters, loadDemoMaster, user } = useHGS();
  const [drawer, setDrawer] = useState(null);   // { mode: 'questions'|'mapping', moduleKey }

  const isAdmin = user?.role === 'admin';

  const handleLoad = (mk) => {
    const result = loadDemoMaster(mk);
    if (result) toast.success('Demo master ingested', {
      description: `${result.fileName} · ${result.added} questions live at ${result.version}`,
    });
  };

  return (
    <div className="px-6 py-6 space-y-6" data-testid="question-bank-page">
      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Master Question Bank</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            The canonical source of every governance item across the HGS stack — Risk
            Universe, Fraud Prevention, Product Compliance and DLC Self-Verification.
            All four masters are mapped 1:1 from HMSI&apos;s official Excel files.
          </p>
        </div>
        {!isAdmin && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-[10px] font-bold uppercase tracking-wider text-amber-900">
            <AlertTriangle className="w-3 h-3" /> Admin-only · view&nbsp;mode
          </div>
        )}
      </div>

      {/* Cards — Risk + Fraud + PCC + DLC side-by-side under one Admin pane */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {MODULES.map((m) => {
          const fx = fixtureFor(m);
          if (!fx) return null;
          const total = fx.rows.length;
          // For ingested-source modules (Fraud/PCC/DLC) "isActive" reflects
          // whether the fixture has been written into the live `masters[k]`
          // store. Risk is `alwaysActive` because it ships baked-in.
          const ingested = m.alwaysActive ? total : (masters[m.key] || []).length;
          const isActive = ingested >= total;
          const Icon = m.icon;
          const sourceLabel = m.alwaysActive ? 'Official HMSI Risk Master' : 'Static JSON Master';
          return (
            <div
              key={m.key}
              className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col"
              data-testid={`qb-card-${m.key}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: m.accent + '15', color: m.accent }}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <StatusPill active={isActive} accent={m.accent} />
              </div>

              <div className="text-base font-bold text-slate-900">{m.title}</div>
              <div className="text-[11px] text-slate-500 leading-snug mb-4">{m.subtitle}</div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <Stat label="Total" value={`${total} ${m.countLabel}`} accent={m.accent} />
                <Stat label="Loaded" value={`${ingested}/${total}`} muted={!isActive} />
              </div>

              <div className="space-y-1.5 mb-4">
                <SourceRow label="File" value={fx.fileName} mono />
                <SourceRow label="Sheets" value={`${fx.sheetNames.length} sheet(s)`} />
                <SourceRow label="Source" value={sourceLabel} />
              </div>

              <div className="mt-auto flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDrawer({ mode: 'questions', moduleKey: m.key })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 text-xs font-semibold rounded-lg transition"
                    data-testid={`qb-view-questions-${m.key}`}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View {m.key === 'risk' ? 'Risks' : 'Questions'}
                  </button>
                  <button
                    onClick={() => setDrawer({ mode: 'mapping', moduleKey: m.key })}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 text-xs font-semibold rounded-lg transition"
                    data-testid={`qb-view-mapping-${m.key}`}
                  >
                    <Code2 className="w-3.5 h-3.5" />
                    View Mapping
                  </button>
                </div>
                {isAdmin && !isActive && !m.alwaysActive && (
                  <button
                    onClick={() => handleLoad(m.key)}
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-white text-xs font-semibold rounded-lg shadow-sm transition hover:opacity-90"
                    style={{ backgroundColor: m.accent }}
                    data-testid={`qb-ingest-${m.key}`}
                  >
                    <Wand2 className="w-3.5 h-3.5" />
                    Ingest into Live Master
                  </button>
                )}
                {m.alwaysActive && (
                  <div
                    className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-slate-50 border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wider rounded-lg"
                    title="Risk Universe is the parent governance layer — always active"
                  >
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    Parent governance layer
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {drawer && (
        <Drawer onClose={() => setDrawer(null)} module={MODULES.find((x) => x.key === drawer.moduleKey)} mode={drawer.mode} />
      )}
    </div>
  );
}

function StatusPill({ active, accent }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider"
      style={{
        backgroundColor: active ? accent + '18' : '#F1F5F9',
        color: active ? accent : '#64748B',
      }}
      data-testid="qb-status-pill"
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: active ? accent : '#94A3B8' }}
      />
      {active ? 'Active Demo Master' : 'Not Loaded'}
    </span>
  );
}

function Stat({ label, value, accent, muted }) {
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-100 p-2.5">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className="text-base font-bold mt-0.5"
        style={{ color: muted ? '#94A3B8' : (accent || '#0F172A') }}
      >
        {value}
      </div>
    </div>
  );
}

function SourceRow({ label, value, mono }) {
  return (
    <div className="flex items-start gap-2 text-[11px]">
      <span className="font-bold text-slate-500 uppercase tracking-wider w-12 flex-shrink-0">{label}</span>
      <span className={`text-slate-700 truncate ${mono ? 'font-mono' : ''}`} title={value}>{value}</span>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Drawer — questions list OR JSON schema mapping
// ────────────────────────────────────────────────────────────────────────────
function Drawer({ onClose, module: m, mode }) {
  const fx = fixtureFor(m);
  const schema = MODULE_SCHEMAS[m.key]; // undefined for Risk — handled below
  const grouped = useMemo(() => {
    const out = {};
    fx.rows.forEach((r) => {
      const cat = r[m.categoryKey] || '—';
      (out[cat] = out[cat] || []).push(r);
    });
    return out;
  }, [fx.rows, m.categoryKey]);

  const label = m.key === 'risk' ? 'All Risks' : 'All Questions';
  const title = mode === 'questions' ? `${m.title} — ${label} (${fx.rows.length})` : `${m.title} — Schema Mapping`;

  return (
    <div className="fixed inset-0 z-50 flex" data-testid={`qb-drawer-${mode}-${m.key}`}>
      <div className="flex-1 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="w-full max-w-2xl bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between" style={{ backgroundColor: m.accent + '08' }}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: m.accent }}>{m.title}</div>
            <div className="text-base font-bold text-slate-900">{title}</div>
            <div className="text-[10px] text-slate-500 mt-0.5 font-mono truncate max-w-[480px]" title={fx.fileName}>
              {fx.fileName} · {fx.sheetNames.length} sheet(s)
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-md transition" data-testid="qb-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {mode === 'questions' ? (
            <div className="space-y-5">
              {Object.entries(grouped).map(([cat, rows]) => (
                <div key={cat}>
                  <div className="flex items-center gap-2 mb-2 sticky top-0 bg-white py-1">
                    <FileSpreadsheet className="w-3.5 h-3.5" style={{ color: m.accent }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700">{cat}</span>
                    <span className="text-[10px] text-slate-400">· {rows.length} item(s)</span>
                  </div>
                  <ol className="space-y-2">
                    {rows.map((r) => (
                      <li key={r[m.idKey]} className="rounded-lg border border-slate-200 p-3 bg-white">
                        <div className="flex items-baseline gap-2 mb-1">
                          <span className="font-mono font-bold text-[11px]" style={{ color: m.accent }}>
                            {r[m.idKey]}
                          </span>
                          {r._sourceRow != null && (
                            <span className="text-[9px] text-slate-400 font-mono">Row {r._sourceRow}</span>
                          )}
                          {r._sourceSheet && (
                            <span className="text-[9px] px-1 py-0 rounded bg-slate-100 text-slate-600 font-medium">
                              {r._sourceSheet}
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] leading-snug text-slate-800">{r[m.questionKey]}</div>
                      </li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          ) : (
            <pre className="text-[10px] leading-snug text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono whitespace-pre-wrap break-words">
              {JSON.stringify(
                {
                  module: m.key,
                  fileName: fx.fileName,
                  sheetNames: fx.sheetNames,
                  schemaFields: schema?.fields
                    ? schema.fields.map((f) => ({ key: f.key, csvHeader: f.csv, type: f.type, mandatory: !!f.mandatory }))
                    : Object.keys(fx.rows[0] || {}).map((k) => ({ key: k, type: typeof (fx.rows[0] || {})[k] })),
                  sampleRow: fx.rows[0],
                },
                null,
                2,
              )}
            </pre>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            {m.key === 'risk'
              ? 'All risks sourced from official HMSI Risk Master'
              : 'All questions sourced from official HMSI master JSON'}
          </span>
          <span>Total: <b className="font-mono text-slate-700">{fx.rows.length}</b></span>
        </div>
      </div>
    </div>
  );
}
